import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CheckCircle, XCircle, Loader } from 'lucide-react';
import { Logo } from '../components/Logo';
import { authApi } from '../services/authApi';

export const EmailVerifyPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setErrorMessage('No verification token found. Please check your email link.');
      return;
    }

    const verify = async () => {
      try {
        await authApi.verifyEmail(token);
        setStatus('success');
      } catch (error: any) {
        setStatus('error');
        setErrorMessage(
          error?.message || 'Invalid or expired verification link. Please request a new one.'
        );
      }
    };

    verify();
  }, [token]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-cyan-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <Logo size="lg" className="mx-auto mb-4" />
        </div>

        <div className="bg-white rounded-2xl shadow-xl border border-medical-200 p-8 text-center">
          {status === 'loading' && (
            <>
              <div className="w-20 h-20 bg-cyan-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Loader className="h-12 w-12 text-cyan-600 animate-spin" />
              </div>
              <h1 className="text-2xl font-bold text-medical-900 mb-4">
                Verifying your email...
              </h1>
              <p className="text-medical-600">
                Please wait a moment.
              </p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="h-12 w-12 text-green-600" />
              </div>
              <h1 className="text-2xl font-bold text-medical-900 mb-4">
                Email Verified!
              </h1>
              <p className="text-medical-600 mb-6">
                Your email address has been successfully verified.
              </p>
              <a
                href="/dashboard"
                className="w-full btn-primary flex items-center justify-center space-x-2 mb-4 no-underline"
              >
                Continue to ClinLexis
              </a>
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-sm text-green-800">
                  If you were signing up in another tab, you can also close this tab and continue there.
                </p>
              </div>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <XCircle className="h-12 w-12 text-red-600" />
              </div>
              <h1 className="text-2xl font-bold text-medical-900 mb-4">
                Verification Failed
              </h1>
              <p className="text-medical-600 mb-6">
                {errorMessage}
              </p>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="text-sm text-amber-800">
                  If your link has expired, sign in and request a new verification email from the pending page.
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
